import { useMemo, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Copy, Image, Share2, Check } from 'lucide-react';
import { toast } from 'sonner';

const SHARE_IMAGE_FORMAT = {
  width: 1080,
  height: 1920,
  aspectClass: 'aspect-[9/16]',
  fileName: 'openruns-invite-image.png',
};

const wrapText = (ctx, text, maxWidth) => {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';

  words.forEach((word) => {
    const testLine = line ? `${line} ${word}` : word;
    if (ctx.measureText(testLine).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = testLine;
    }
  });

  if (line) lines.push(line);
  return lines;
};

const wrapTextWithLimit = (ctx, text, maxWidth, maxLines) => {
  const lines = wrapText(ctx, text, maxWidth);
  if (!maxLines || lines.length <= maxLines) return lines;

  const limited = lines.slice(0, maxLines);
  let lastLine = limited[limited.length - 1] || '';
  while (lastLine && ctx.measureText(`${lastLine}...`).width > maxWidth) {
    lastLine = lastLine.replace(/\s+\S*$/, '');
    if (lastLine === limited[limited.length - 1]) {
      lastLine = lastLine.slice(0, -1);
    }
  }
  limited[limited.length - 1] = `${lastLine || limited[limited.length - 1].slice(0, 8)}...`;
  return limited;
};

const roundRect = (ctx, x, y, width, height, radius) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

const drawPill = (ctx, text, x, y, fill, color) => {
  ctx.font = '700 34px Arial, sans-serif';
  const width = Math.ceil(ctx.measureText(text).width) + 56;
  roundRect(ctx, x, y, width, 68, 34);
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.fillStyle = color;
  ctx.fillText(text, x + 28, y + 45);
};

const drawCourtTexture = (ctx, width, height) => {
  ctx.save();
  ctx.strokeStyle = 'rgba(255,255,255,0.10)';
  ctx.lineWidth = 8;

  ctx.beginPath();
  ctx.moveTo(width * 0.08, height * 0.48);
  ctx.lineTo(width * 0.92, height * 0.48);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(width * 0.5, height * 0.48, width * 0.18, 0, Math.PI * 2);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(width * 0.5, height * 1.02, width * 0.42, Math.PI, Math.PI * 2);
  ctx.stroke();

  ctx.strokeStyle = 'rgba(249,115,22,0.28)';
  ctx.lineWidth = 18;
  ctx.beginPath();
  ctx.arc(width * 0.86, height * 0.12, width * 0.22, 0, Math.PI * 2);
  ctx.stroke();

  ctx.globalAlpha = 0.09;
  for (let x = -width; x < width * 2; x += 72) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x + width * 0.7, height);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.stroke();
  }
  ctx.restore();
};

const drawDetailChip = (ctx, text, x, y, maxWidth) => {
  const label = String(text || '').trim();
  if (!label) return;
  ctx.font = '700 30px Arial, sans-serif';
  const width = Math.min(maxWidth, Math.ceil(ctx.measureText(label).width) + 44);
  roundRect(ctx, x, y, width, 62, 18);
  ctx.fillStyle = 'rgba(255,255,255,0.10)';
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.fillText(label, x + 22, y + 40);
};

const drawTextLines = (ctx, lines, x, startY, lineHeight) => {
  let y = startY;
  lines.forEach((line) => {
    ctx.fillText(line, x, y);
    y += lineHeight;
  });
  return y;
};

const getCanvasBlob = (canvas) => new Promise((resolve) => {
  canvas.toBlob((blob) => resolve(blob), 'image/png', 0.95);
});

const getShortJoinLinkLabel = (shareUrl) => {
  const rawUrl = String(shareUrl || '').trim();
  if (!rawUrl) return '';

  try {
    const parsed = new URL(rawUrl);
    if (/^\/j\/[^/]+$/i.test(parsed.pathname)) {
      return `${parsed.host}${parsed.pathname}`;
    }
  } catch {
    const match = rawUrl.match(/(?:https?:\/\/)?([^/\s]+\/j\/[A-Za-z0-9]+)/);
    if (match) return match[1];
  }

  return '';
};

const getInviteAvailability = (spotsLabel, capacityLabel) => {
  const spotText = String(spotsLabel || '').trim();
  if (
    /spots?\s+open/i.test(spotText)
    || /^waitlist\s+open$/i.test(spotText)
    || /^currently\s+full$/i.test(spotText)
  ) {
    return spotText;
  }

  const capacityMatch = String(capacityLabel || '').match(/(\d+)\s+of\s+(\d+)/i);
  if (capacityMatch) {
    const joined = Number(capacityMatch[1]);
    const max = Number(capacityMatch[2]);
    const spotsOpen = Math.max(0, max - joined);
    return `${spotsOpen} ${spotsOpen === 1 ? 'spot open' : 'spots open'}`;
  }

  return spotText || String(capacityLabel || '').trim();
};

const generateSocialImage = async ({
  headline,
  publicShareMessage,
  typeLabel,
  dateLabel,
  timeLabel,
  locationLabel,
  capacityLabel,
  spotsLabel,
  shareUrl
}) => {
  const { width, height } = SHARE_IMAGE_FORMAT;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  const bg = ctx.createLinearGradient(0, 0, width, height);
  bg.addColorStop(0, '#050505');
  bg.addColorStop(0.42, '#111113');
  bg.addColorStop(0.76, '#20130b');
  bg.addColorStop(1, '#f97316');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, width, height);

  drawCourtTexture(ctx, width, height);

  ctx.save();
  ctx.globalAlpha = 0.28;
  ctx.fillStyle = '#f97316';
  ctx.beginPath();
  ctx.moveTo(width * 0.72, 0);
  ctx.lineTo(width, 0);
  ctx.lineTo(width, height);
  ctx.lineTo(width * 0.48, height);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  const margin = 70;
  roundRect(ctx, margin, margin, width - margin * 2, height - margin * 2, 54);
  ctx.fillStyle = 'rgba(10,10,10,0.74)';
  ctx.fill();

  ctx.strokeStyle = 'rgba(249,115,22,0.72)';
  ctx.lineWidth = 5;
  ctx.stroke();

  ctx.fillStyle = '#f97316';
  roundRect(ctx, 118, 245, 262, 72, 22);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.font = '900 34px Arial, sans-serif';
  ctx.fillText('OpenRuns', 146, 292);

  drawPill(ctx, typeLabel, 690, 245, 'rgba(255,255,255,0.12)', '#ffffff');

  let y = 390;
  const contentX = 118;
  const contentWidth = width - 236;
  const shareMessage = String(publicShareMessage || '').trim();

  if (shareMessage) {
    ctx.fillStyle = '#f97316';
    roundRect(ctx, contentX, y - 42, 92, 10, 5);
    ctx.fill();

    ctx.font = '900 74px Arial, sans-serif';
    ctx.fillStyle = '#ffffff';
    const messageLines = wrapTextWithLimit(ctx, shareMessage, contentWidth, 4);
    y = drawTextLines(ctx, messageLines, contentX, y + 44, 82) + 28;

    ctx.fillStyle = 'rgba(249,115,22,0.55)';
    roundRect(ctx, contentX, y, contentWidth, 4, 2);
    ctx.fill();
    y += 72;

    ctx.font = '900 46px Arial, sans-serif';
    ctx.fillStyle = '#fed7aa';
    const titleLines = wrapTextWithLimit(ctx, headline, contentWidth, 2);
    y = drawTextLines(ctx, titleLines, contentX, y, 54) + 24;
  } else {
    ctx.font = '900 90px Arial, sans-serif';
    ctx.fillStyle = '#ffffff';
    const headlineLines = wrapTextWithLimit(ctx, headline, contentWidth, 5);
    y = drawTextLines(ctx, headlineLines, contentX, y + 60, 98) + 30;
  }

  const detailsTop = Math.min(Math.max(y + 18, 900), 1080);
  roundRect(ctx, contentX, detailsTop, contentWidth, 300, 32);
  ctx.fillStyle = 'rgba(255,255,255,0.12)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.12)';
  ctx.lineWidth = 2;
  ctx.stroke();

  ctx.font = '900 42px Arial, sans-serif';
  ctx.fillStyle = '#ffffff';
  const dateTime = [dateLabel, timeLabel].filter(Boolean).join(' at ');
  const detailLines = wrapTextWithLimit(ctx, dateTime, contentWidth - 84, 2);
  let detailY = detailsTop + 72;
  detailLines.forEach((line) => {
    ctx.fillText(line, 160, detailY);
    detailY += 50;
  });

  ctx.font = '800 34px Arial, sans-serif';
  ctx.fillStyle = '#f4f4f5';
  wrapTextWithLimit(ctx, locationLabel, contentWidth - 84, 2).forEach((line) => {
    ctx.fillText(line, 160, detailY);
    detailY += 42;
  });

  const chipTop = detailsTop + 328;
  drawDetailChip(ctx, capacityLabel, 118, chipTop, 470);
  drawDetailChip(ctx, spotsLabel, 610, chipTop, 352);

  const shortJoinLink = getShortJoinLinkLabel(shareUrl);
  const ctaY = 1370;
  roundRect(ctx, 118, ctaY, width - 236, shortJoinLink ? 188 : 132, 42);
  ctx.fillStyle = '#f97316';
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.22)';
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.fillStyle = '#ffffff';
  ctx.font = '900 42px Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(shortJoinLink ? 'Join:' : 'Join on OpenRuns', width / 2, ctaY + 62);
  if (shortJoinLink) {
    ctx.font = '900 48px Arial, sans-serif';
    ctx.fillText(shortJoinLink, width / 2, ctaY + 130);
  }
  ctx.textAlign = 'left';

  return getCanvasBlob(canvas);
};

const InstagramShareSheet = ({
  open,
  onOpenChange,
  headline = '',
  publicShareMessage = '',
  typeLabel = 'Open Run',
  dateLabel = '',
  timeLabel = '',
  locationLabel = '',
  capacityLabel = '',
  spotsLabel = '',
  shareUrl,
  onTrack
}) => {
  const [inviteCopied, setInviteCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);
  const [clickableShareBusy, setClickableShareBusy] = useState(false);

  const inviteText = useMemo(() => {
    const message = String(publicShareMessage || '').trim();
    const title = String(headline || typeLabel || '').trim();
    const dateTime = [dateLabel, timeLabel].filter(Boolean).join(' at ');
    const location = String(locationLabel || '').trim();
    const availability = getInviteAvailability(spotsLabel, capacityLabel);
    const lines = [];

    if (message) {
      lines.push(message, '');
    }

    if (title) lines.push(title);
    if (dateTime) lines.push(dateTime);
    if (location) lines.push(location);
    if (availability) lines.push(availability);

    if (shareUrl) {
      lines.push('', 'Join here:', shareUrl);
    }

    return lines.join('\n');
  }, [capacityLabel, dateLabel, headline, locationLabel, publicShareMessage, shareUrl, spotsLabel, timeLabel, typeLabel]);

  const shareCaption = useMemo(() => {
    const message = String(publicShareMessage || '').trim();
    const lines = [];
    if (message) {
      lines.push(message, '');
    }
    if (shareUrl) {
      lines.push('Join:', shareUrl);
    }
    return lines.join('\n');
  }, [publicShareMessage, shareUrl]);

  const shortJoinLink = getShortJoinLinkLabel(shareUrl);

  const handleCopyInviteText = async () => {
    try {
      await navigator.clipboard.writeText(inviteText);
      toast.success('Invite text copied');
      setInviteCopied(true);
      onTrack?.('invite_text_copied');
      setTimeout(() => setInviteCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy automatically. Please copy manually.");
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Join link copied');
      setLinkCopied(true);
      onTrack?.('join_link_copied');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy automatically. Please copy manually.");
    }
  };

  const handleSendClickableInvite = async () => {
    setClickableShareBusy(true);
    try {
      if (navigator.share) {
        await navigator.share({
          title: headline || 'OpenRuns invite',
          text: inviteText,
          url: shareUrl || undefined
        });
        onTrack?.('clickable_invite_shared');
        return;
      }

      await navigator.clipboard.writeText(inviteText);
      toast.success('Invite text copied');
      onTrack?.('invite_text_copied');
    } catch (err) {
      if (err?.name !== 'AbortError') {
        toast.error("Couldn't share automatically. Copy the invite text instead.");
      }
    } finally {
      setClickableShareBusy(false);
    }
  };

  const getImageBlob = () => generateSocialImage({
    headline,
    publicShareMessage,
    typeLabel,
    dateLabel,
    timeLabel,
    locationLabel,
    capacityLabel,
    spotsLabel,
    shareUrl
  });

  const downloadBlob = (blob) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = SHARE_IMAGE_FORMAT.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleShareImage = async () => {
    setImageBusy(true);
    try {
      if (shareUrl) {
        try {
          await navigator.clipboard.writeText(shareUrl);
          toast.success('Link copied. In Instagram Story, tap Sticker → Link → Paste.');
          setLinkCopied(true);
          onTrack?.('join_link_copied');
          setTimeout(() => setLinkCopied(false), 2000);
        } catch {
          toast.info('Image ready. Use Copy Join Link if you want to share the link separately.');
        }
      }

      const blob = await getImageBlob();
      if (!blob) throw new Error('Image export failed');
      const file = new File([blob], SHARE_IMAGE_FORMAT.fileName, { type: 'image/png' });

      if (navigator.canShare?.({ files: [file] })) {
        onTrack?.('share_image_shared');
        await navigator.share({
          title: 'OpenRuns',
          text: shareCaption,
          files: [file]
        });
        return;
      }

      downloadBlob(blob);
      onTrack?.('share_image_shared');
      toast.info('Image sharing is not supported here. Image downloaded instead.');
    } catch (err) {
      if (err?.name !== 'AbortError') {
        toast.error('Could not share image.');
      }
    } finally {
      setImageBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto rounded-t-[28px] px-5 pb-7 pt-4">
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-zinc-200" />
        <SheetHeader className="mb-5 text-left">
          <SheetTitle className="text-xl font-black text-zinc-950">Share Invite</SheetTitle>
        </SheetHeader>

        <div className="mb-5 rounded-2xl bg-orange-50 p-4 text-sm leading-6 text-orange-950">
          Send a clickable invite for messages and DMs. Instagram images are not clickable; for Stories, add the copied link as a Link Sticker.
        </div>

        <div className={`mx-auto mb-5 w-full max-w-[280px] overflow-hidden rounded-[24px] bg-zinc-950 p-4 text-white shadow-xl ${SHARE_IMAGE_FORMAT.aspectClass}`}>
          <div className="relative flex h-full flex-col overflow-hidden rounded-[18px] border border-orange-500/60 bg-zinc-950 p-4 text-white">
            <div className="absolute -right-12 -top-10 h-32 w-32 rounded-full border-[10px] border-orange-500/30" />
            <div className="absolute left-0 top-1/2 h-px w-full bg-white/10" />
            <div className="relative mb-8 mt-8 flex items-center justify-between">
              <span className="rounded-lg bg-orange-500 px-3 py-2 text-sm font-black">OpenRuns</span>
              <span className="rounded-full bg-white/10 px-2 py-1 text-[10px] font-bold uppercase text-white">
                {typeLabel}
              </span>
            </div>
            <div className="relative flex-1 space-y-4">
              {publicShareMessage && (
                <p className="max-h-40 overflow-hidden border-l-4 border-orange-500 pl-3 text-2xl font-black leading-[1.02] text-white">
                  {publicShareMessage}
                </p>
              )}
              <h3 className={`${publicShareMessage ? 'text-xl text-orange-100' : 'text-3xl text-white'} font-black leading-[0.95]`}>
                {headline}
              </h3>
              <div className="rounded-2xl bg-white/10 p-3 text-xs font-bold leading-relaxed">
                <p>{[dateLabel, timeLabel].filter(Boolean).join(' at ')}</p>
                <p className="mt-1">{locationLabel}</p>
              </div>
              <div className="flex flex-wrap gap-2 text-[10px] font-bold">
                <span className="rounded-full bg-white/10 px-2 py-1">{capacityLabel}</span>
                <span className="rounded-full bg-white/10 px-2 py-1">{spotsLabel}</span>
              </div>
            </div>
            <div className="relative mb-16 rounded-2xl bg-orange-500 px-4 py-3 text-center text-sm font-black text-white">
              {shortJoinLink ? (
                <>
                  <p>Join:</p>
                  <p className="mt-1 break-all text-base">{shortJoinLink}</p>
                </>
              ) : (
                'Join on OpenRuns'
              )}
            </div>
          </div>
        </div>

        <Button
          type="button"
          onClick={handleSendClickableInvite}
          disabled={clickableShareBusy}
          className="mb-3 h-14 w-full rounded-2xl bg-orange-500 text-base font-bold hover:bg-orange-600 gap-2"
        >
          <Share2 className={`h-5 w-5 ${clickableShareBusy ? 'animate-pulse' : ''}`} />
          Send Clickable Invite
        </Button>

        <Button
          type="button"
          onClick={handleShareImage}
          disabled={imageBusy}
          variant="outline"
          className="mb-3 h-14 w-full rounded-2xl text-base font-bold gap-2"
        >
          {imageBusy ? <Image className="h-5 w-5 animate-pulse" /> : <Share2 className="h-5 w-5" />}
          Share Story Image + Copy Link
        </Button>

        <Button
          type="button"
          onClick={handleCopyInviteText}
          variant="outline"
          className="mb-3 h-14 w-full rounded-2xl text-base font-bold gap-2"
        >
          {inviteCopied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
          Copy Invite Text
        </Button>

        <Button
          type="button"
          onClick={handleCopyLink}
          variant="outline"
          className="mb-4 h-14 w-full rounded-2xl text-base font-bold gap-2"
        >
          {linkCopied ? <Check className="h-5 w-5 text-green-600" /> : <Copy className="h-5 w-5" />}
          Copy Join Link
        </Button>

        <div className="rounded-2xl bg-zinc-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Invite text</p>
          <p className="mt-2 whitespace-pre-line text-sm leading-6 text-zinc-700">{inviteText}</p>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default InstagramShareSheet;
